import {
  createContext,
  type DragEvent,
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type DropEvent, type FileRejection, type FileWithPath, useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';

import { useTrackClickedToOpenImportFromDeviceModal, useTrackInitiateUpload } from '@air/analytics';
import { Portal } from '@air/primitive-portal';
import { useModalContext } from '@air/provider-modal';
import { reportErrorToBugsnag } from '@air/utils-error';

import { ImageSearchZoneOverlay } from '~/components/PrivateDropzone/components/ImageSearchZoneOverlay';
import { MultipleZoneOverlay } from '~/components/PrivateDropzone/components/MultipleZoneOverlay';
import { OverlayContainer } from '~/components/PrivateDropzone/components/OverlayContainer';
import { SingleZoneOverlay } from '~/components/PrivateDropzone/components/SingleZoneOverlay';
import {
  useEditCanvasDropzoneActive,
  useEditCanvasDropzoneFullscreen,
} from '~/components/PrivateDropzone/EditCanvasDropzoneContext';
import { type DropZone } from '~/components/PrivateDropzone/types';
import { WORKSPACE_CANVAS_HORIZONTAL_PADDING, WORKSPACE_MIN_WIDTH } from '~/constants/panelWidths';
import { centralizedClipIdSelector } from '~/store/centralizedClip/selectors';
import { isDevOrTestStage } from '~/swr-hooks/utils';
import { distanceToRect, pointWithinRect } from '~/utils/DragUtils';

const DEFAULT_PROXIMITY_THRESHOLD = 150;
const UPLOAD_INPUT = 'UPLOAD_INPUT';
export type UploadInputType = 'files' | 'folders';

export type UploadContextType = {
  openUploader: (params?: { type?: UploadInputType }) => void;
};

export const UploadContext = createContext<UploadContextType>({
  openUploader: () => {
    const error = new Error('UploadContext is not provided');

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error.message,
      });
    }
  },
});

export type OnUploadParams = {
  acceptedFiles: FileWithPath[];
  rejectedFiles: FileWithPath[];
  event: DropEvent | null;
  dropZone: DropZone | null;
};

export type UploadDropzoneProps = {
  onUpload: (params: OnUploadParams) => void;
  disabled?: boolean;
  children?: ReactNode;
  supportsDirectUpload: boolean;
  supportsSimilarToImageSearch: boolean;
  currentBoardTitle?: string;
  currentWorkspaceName?: string;
};

export const UploadDropzone = memo(
  ({
    onUpload,
    children,
    disabled = false,
    supportsDirectUpload,
    supportsSimilarToImageSearch,
    currentBoardTitle,
    currentWorkspaceName,
  }: UploadDropzoneProps) => {
    const { trackInitiateUpload } = useTrackInitiateUpload();
    const { trackClickedToOpenImportFromDeviceModal } = useTrackClickedToOpenImportFromDeviceModal();
    const { dispatch: modalDispatch } = useModalContext();
    const clipId = useSelector(centralizedClipIdSelector);
    const isAssetModalOpen = !!clipId;
    const isEditCanvasActive = useEditCanvasDropzoneActive();
    const isEditCanvasFullscreen = useEditCanvasDropzoneFullscreen();

    const [hoveredZone, setHoveredZone] = useState<DropZone | null>(null);
    const [inputType, setInputType] = useState<UploadInputType | null>(null);
    const [searchCommandTriggerPosition, setSearchCommandTriggerPosition] = useState<DOMRect | null>(null);
    const [showSearchByImageOverlay, setShowSearchByImageOverlay] = useState(false);
    const dropZoneChooseBoardRef = useRef<HTMLDivElement | null>(null);
    const dropZoneUploadRef = useRef<HTMLDivElement | null>(null);
    const dropZoneSearchByImageRef = useRef<HTMLDivElement | null>(null);

    const determineDropZone = useCallback(
      (clientX: number, clientY: number): DropZone | null => {
        const point = { x: clientX, y: clientY };

        if (supportsDirectUpload && dropZoneUploadRef.current) {
          if (pointWithinRect(point, dropZoneUploadRef.current.getBoundingClientRect())) {
            return 'upload';
          }
        } else if (supportsSimilarToImageSearch && dropZoneSearchByImageRef.current) {
          if (pointWithinRect(point, dropZoneSearchByImageRef.current.getBoundingClientRect())) {
            return 'search-by-image';
          }
        }
        if (dropZoneChooseBoardRef.current) {
          if (pointWithinRect(point, dropZoneChooseBoardRef.current.getBoundingClientRect())) {
            return 'choose-board';
          }
        }

        return null;
      },
      [supportsDirectUpload, supportsSimilarToImageSearch],
    );

    const handleDrop = useCallback(
      (acceptedFiles: FileWithPath[], fileRejections: FileRejection[], event: DropEvent | null) => {
        setHoveredZone(null);
        setInputType(null);

        // When the edit canvas is active and the cursor wasn't over the workspace
        // zone, the drop was handled by EditCanvasFileDropzone — skip workspace upload.
        // In fullscreen there is no workspace zone at all.
        if (isEditCanvasActive && (hoveredZone === null || isEditCanvasFullscreen)) {
          return;
        }

        // When canvas is open in split-screen and user is already on a board, upload
        // directly without prompting — the workspace sidebar may be closed so we can't
        // rely on zone detection to get an 'upload' dropZone value.
        const effectiveDropZone = isEditCanvasActive && supportsDirectUpload ? 'upload' : hoveredZone;

        onUpload({
          acceptedFiles,
          event,
          rejectedFiles: fileRejections.map(({ file }) => file),
          dropZone: effectiveDropZone,
        });

        let eventType: 'drag-drop' | 'new-button' | undefined;

        if (event?.type === 'drop') {
          eventType = 'drag-drop';
        } else if (event?.type === 'change') {
          eventType = 'new-button';
        }

        if (eventType) {
          trackInitiateUpload({ location: eventType });
        }
      },
      [hoveredZone, isEditCanvasActive, isEditCanvasFullscreen, onUpload, supportsDirectUpload, trackInitiateUpload],
    );

    const handleDragOver = useCallback(
      (event: DragEvent<HTMLElement>) => {
        const x = event.clientX;
        const y = event.clientY;

        // When the edit canvas is active, clear hover if the cursor is over the
        // right panel area (handled by EditCanvasFileDropzone).
        if (isEditCanvasActive) {
          const storedWidth = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--right-sidebar-width') || '0',
            10,
          );
          const leftNavWidth = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--left-nav-width') || '0',
            10,
          );
          // Mirror the min() constraint from WorkspaceRightPanel so the boundary
          // stays accurate when the browser window is narrower than the stored width.
          const rightPanelWidth = Math.min(
            storedWidth,
            window.innerWidth - leftNavWidth - WORKSPACE_MIN_WIDTH - WORKSPACE_CANVAS_HORIZONTAL_PADDING,
          );

          if (x > window.innerWidth - rightPanelWidth) {
            if (hoveredZone !== null) {
              setHoveredZone(null);
            }

            return;
          }
        }

        const zone = determineDropZone(x, y);

        if (zone !== hoveredZone) {
          setHoveredZone(zone);
        }

        if (zone !== 'search-by-image' && supportsSimilarToImageSearch) {
          setShowSearchByImageOverlay(false);
        }

        if (searchCommandTriggerPosition && supportsSimilarToImageSearch) {
          const distance = distanceToRect({ x, y }, searchCommandTriggerPosition);

          if (distance < DEFAULT_PROXIMITY_THRESHOLD) {
            setShowSearchByImageOverlay(true);
          }
        }
      },
      [determineDropZone, hoveredZone, isEditCanvasActive, supportsSimilarToImageSearch, searchCommandTriggerPosition],
    );

    const handleDragLeave = useCallback(() => {
      setHoveredZone(null);
    }, []);

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
      disabled,
      noClick: true,
      noKeyboard: true,
      onDrop: handleDrop,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onFileDialogCancel: () => setInputType(null),
      useFsAccessApi: false,
    });

    const openRef = useRef(open);
    openRef.current = open;

    // Only open the uploader when the inputType changes
    useEffect(() => {
      if (inputType && openRef.current) {
        openRef.current();
      }
    }, [inputType]);

    const openUploader = useCallback(
      (params?: { type?: UploadInputType }) => {
        trackClickedToOpenImportFromDeviceModal();
        setInputType(params?.type ?? 'files');
      },
      [trackClickedToOpenImportFromDeviceModal],
    );

    const contextValue = useMemo(
      () => ({
        openUploader,
      }),
      [openUploader],
    );

    useEffect(() => {
      if (isDragActive) {
        // Close all Air modals managed by ModalProvider to prevent interaction conflicts during drag
        modalDispatch({ type: 'hide-all' });

        // Close all Radix UI components (Popover, DropdownMenu, ContextMenu, etc.) by simulating
        // an Escape keypress. Radix components listen for Escape to close, and this ensures a clean
        // drag experience without UI elements stealing focus or partially covering the drop zones.
        //
        // Exceptions:
        // - If the asset modal is open, we don't want to close it
        // - If the similar to image search is available, we don't want to close it
        //   because the user can open the similar to image search by dragging over the search by image zone
        const forceCloseRadixUI = !isAssetModalOpen && !supportsSimilarToImageSearch;

        if (forceCloseRadixUI) {
          const escapeDown = new KeyboardEvent('keydown', {
            key: 'Escape',
            code: 'Escape',
            keyCode: 27,
            bubbles: true,
            cancelable: true,
          });
          document.dispatchEvent(escapeDown);

          // Dispatch a matching keyup so that libraries tracking pressed keys
          // (e.g. react-hotkeys-hook) remove 'Escape' from their internal set.
          // Without this, the orphaned keydown poisons future key matching.
          const escapeUp = new KeyboardEvent('keyup', {
            key: 'Escape',
            code: 'Escape',
            keyCode: 27,
            bubbles: true,
            cancelable: true,
          });
          document.dispatchEvent(escapeUp);
        }

        const preventScroll = (e: WheelEvent | TouchEvent) => {
          e.preventDefault();
        };

        window.addEventListener('wheel', preventScroll, { passive: false });
        window.addEventListener('touchmove', preventScroll, { passive: false });

        return () => {
          window.removeEventListener('wheel', preventScroll);
          window.removeEventListener('touchmove', preventScroll);
        };
      }
    }, [isDragActive, modalDispatch, isAssetModalOpen, supportsSimilarToImageSearch]);

    useEffect(() => {
      const searchCommandTrigger = document.getElementById('SEARCH_COMMAND_TRIGGER');

      if (searchCommandTrigger && supportsSimilarToImageSearch && isDragActive) {
        setSearchCommandTriggerPosition(searchCommandTrigger.getBoundingClientRect());
      }
    }, [supportsSimilarToImageSearch, isDragActive]);

    let overlayContent: ReactNode = null;

    if (isEditCanvasActive) {
      overlayContent = (
        <OverlayContainer
          className="left-[var(--left-nav-width,0px)] w-[max(calc(100vw-var(--right-sidebar-width)-var(--left-nav-width,0px)),316px)]"
          data-testid="CANVAS_ACTIVE_WORKSPACE_ZONE_OVERLAY"
        >
          <SingleZoneOverlay
            hoveredZone={hoveredZone}
            currentWorkspaceName={currentWorkspaceName}
            dropZoneChooseBoardRef={dropZoneChooseBoardRef}
            className="top-16 bottom-10"
          />
        </OverlayContainer>
      );
    } else if (supportsDirectUpload) {
      overlayContent = (
        <OverlayContainer data-testid="MULTIPLE_ZONE_OVERLAY">
          <MultipleZoneOverlay
            hoveredZone={hoveredZone}
            currentBoardTitle={currentBoardTitle}
            currentWorkspaceName={currentWorkspaceName}
            dropZoneChooseBoardRef={dropZoneChooseBoardRef}
            dropZoneUploadRef={dropZoneUploadRef}
          />
        </OverlayContainer>
      );
    } else if (supportsSimilarToImageSearch) {
      overlayContent = (
        <OverlayContainer data-testid="IMAGE_SEARCH_AND_SINGLE_ZONE_OVERLAY">
          <ImageSearchZoneOverlay
            hoveredZone={hoveredZone}
            dropZoneSearchByImageRef={dropZoneSearchByImageRef}
            showSearchByImageOverlay={showSearchByImageOverlay}
            searchCommandTriggerPosition={searchCommandTriggerPosition}
            dropzoneDescription="Upload a JPG or PNG to search for visually similar assets."
          />
          <SingleZoneOverlay
            hoveredZone={hoveredZone}
            currentWorkspaceName={currentWorkspaceName}
            dropZoneChooseBoardRef={dropZoneChooseBoardRef}
          />
        </OverlayContainer>
      );
    } else {
      overlayContent = (
        <OverlayContainer data-testid="SINGLE_ZONE_OVERLAY">
          <SingleZoneOverlay
            hoveredZone={hoveredZone}
            currentWorkspaceName={currentWorkspaceName}
            dropZoneChooseBoardRef={dropZoneChooseBoardRef}
          />
        </OverlayContainer>
      );
    }

    return (
      <div data-testid="UPLOAD_DROPZONE" className="relative h-full" {...getRootProps()}>
        {inputType === 'folders' ? (
          <input
            {...getInputProps()}
            // @ts-expect-error - https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/webkitdirectory - "directory" is a nonstandard prop but is the only way to do it 💁
            directory=""
            mozdirectory=""
            type="file"
            webkitdirectory=""
            data-testid={UPLOAD_INPUT}
          />
        ) : (
          <input {...getInputProps()} data-testid={UPLOAD_INPUT} />
        )}
        {isDragActive && !disabled && !isAssetModalOpen && !(isEditCanvasActive && isEditCanvasFullscreen) && (
          <Portal>{overlayContent}</Portal>
        )}
        <UploadContext.Provider value={contextValue}>{children}</UploadContext.Provider>
      </div>
    );
  },
);

UploadDropzone.displayName = 'UploadDropzone';
